// Deploy-metadata-driven redirect from the `pages.dev` production alias to
// the canonical hosted origin (web-studio-auth-gate decision D4). Session
// cookies are same-site `SameSite=Lax` against `cloud.jacqos.io`, so only
// the canonical `app.jacqos.io` origin can carry them; the production
// alias hands visitors over. The redirect is keyed on an EXACT host match
// with the production alias, so per-branch preview hosts
// (`<hash>.jacqos-web-studio.pages.dev`), localhost, and the canonical
// origin itself never redirect. Every failure path (missing metadata,
// malformed origin, network error) resolves to "no redirect".

export const PRODUCTION_ALIAS_HOST = "jacqos-web-studio.pages.dev";
export const DEPLOY_METADATA_PATH = "/web-studio-deploy-metadata.json";

export function shouldRedirectToCanonicalOrigin(
  currentHost: string,
  canonicalOrigin: string | null | undefined,
): string | null {
  if (currentHost !== PRODUCTION_ALIAS_HOST) {
    return null;
  }
  if (typeof canonicalOrigin !== "string" || canonicalOrigin.length === 0) {
    return null;
  }
  let canonical: URL;
  try {
    canonical = new URL(canonicalOrigin);
  } catch {
    return null;
  }
  if (canonical.protocol !== "https:") {
    return null;
  }
  if (canonical.host === currentHost) {
    return null;
  }
  return canonical.origin;
}

export type RedirectLocation = Pick<Location, "host" | "pathname" | "search" | "hash">;

// Resolves the full redirect URL (path + query + hash preserved) for the
// current location, reading `canonicalOrigin` from the deploy metadata the
// artifact ships next to itself. Only the production alias host pays the
// metadata fetch; every other host resolves to null immediately.
export async function resolveCanonicalOriginRedirect(
  location: RedirectLocation,
  fetchMetadata: typeof fetch = (input, init) => fetch(input, init),
): Promise<string | null> {
  if (location.host !== PRODUCTION_ALIAS_HOST) {
    return null;
  }
  try {
    const response = await fetchMetadata(DEPLOY_METADATA_PATH, {
      cache: "no-store",
      credentials: "omit",
    });
    if (!response.ok) {
      return null;
    }
    const metadata: unknown = await response.json();
    const canonicalOrigin =
      typeof metadata === "object" &&
      metadata !== null &&
      "canonicalOrigin" in metadata &&
      typeof (metadata as { canonicalOrigin?: unknown }).canonicalOrigin === "string"
        ? (metadata as { canonicalOrigin: string }).canonicalOrigin
        : null;
    const target = shouldRedirectToCanonicalOrigin(location.host, canonicalOrigin);
    if (target === null) {
      return null;
    }
    return `${target}${location.pathname}${location.search}${location.hash}`;
  } catch {
    return null;
  }
}
